import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Paper, Grid, CircularProgress, Tabs, Tab, FormGroup, FormControlLabel, Switch, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Link from '@mui/material/Link';
import LoadingScreen from '../components/common/LoadingScreen';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';

/* global Paddle */

const PricingPlans = () => {
  const { user } = useAuth();
  const { subscription } = useCredits();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly', 'yearly', 'lifetime'
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '', details: '' });
  const navigate = useNavigate();

  // Handle billing cycle change
  const handleBillingCycleChange = (event, newValue) => {
    setBillingCycle(newValue);
  };

  // Load Paddle
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    
    script.onload = () => {
      // Comment out or remove sandbox environment setting for production
      // Paddle.Environment.set("sandbox");
      
      console.log('Initializing Paddle with token:', 'live_2bbf6a61fd70a9a9abc9655b2a5');
      
      Paddle.Initialize({
        token: 'live_2bbf6a61fd70a9a9abc9655b2a5',
        eventCallback: function(event) {
          console.log('Paddle event:', event);
        }
      });
      setLoading(false);
      
      // Get user ID for event listener
      const token = localStorage.getItem('token');
      const decodedToken = token ? JSON.parse(atob(token.split('.')[1])) : null;
      const userId = decodedToken?.userId;
      
      // Add event listener for checkout events - attach once when Paddle loads
      window.addEventListener('paddle:checkout:completed', (event) => {
        console.log('Paddle checkout completed event:', event);
        console.log('Paddle checkout data:', event.data);
        
        try {
          // Capture complete data
          const completeData = {
            ...event.data,
            userId,
            timestamp: new Date().toISOString()
          };
          
          // Log to console for debugging
          console.log('Complete checkout data:', completeData);
          
          // Send data to server for logging
          axios.post(
            `${process.env.REACT_APP_API_URL}/api/payments/log-checkout`, 
            completeData,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          ).then(() => {
            console.log('Checkout data sent to server');
          }).catch(err => {
            console.error('Error sending checkout data to server:', err);
          });
          
          // Optionally save to localStorage for debugging
          localStorage.setItem('paddle_checkout_data', JSON.stringify(completeData));
        } catch (logError) {
          console.error('Error logging checkout data:', logError);
        }
      });
    };

    document.body.appendChild(script);

    // Add pulse animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(56, 161, 105, 0.4);
        }
        70% {
          box-shadow: 0 0 0 6px rgba(56, 161, 105, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(56, 161, 105, 0);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/payments/subscription`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.subscription && response.data.subscription.status === 'active') {
          setCurrentPlan(response.data.subscription.price_id);
        } else {
          setCurrentPlan('free');
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        setCurrentPlan('free');
      }
    };
    
    if (user) {
      checkSubscription();
    }
  }, [user]);

  // Fetch subscription details
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      setSubscriptionLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/payments/subscription`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (response.data.subscription?.status === 'active') {
          let endDateMessage = '';
          let dateToFormat = null;

          if (response.data.subscription.cancellation_date) {
            dateToFormat = new Date(response.data.subscription.cancellation_date);
            endDateMessage = 'Your subscription will cancel on: ';
          } else if (response.data.subscription.next_billed_at) {
            dateToFormat = new Date(response.data.subscription.next_billed_at);
            endDateMessage = 'Your subscription will renew on: ';
          } else if (response.data.subscription.created_at) { // Fallback if next_billed_at is not available
            const createdDate = new Date(response.data.subscription.created_at);
            dateToFormat = new Date(createdDate);
            dateToFormat.setMonth(dateToFormat.getMonth() + 1);
            endDateMessage = 'Your subscription renews approximately on: ';
          }

          if (dateToFormat) {
            const formattedDate = dateToFormat.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
            // We'll store the full message in subscriptionEndDate or create a new state for the prefix
            // For now, let's assume subscriptionEndDate will store the date part,
            // and we'll handle the prefix "cancel on" vs "renew on" in the JSX.
            // Or, more simply, store the full message:
            setSubscriptionEndDate(endDateMessage + formattedDate);
          } else {
            setSubscriptionEndDate('Unable to determine subscription end date.');
          }
        } else {
          setSubscriptionEndDate(null); // Clear if no active subscription
        }
      } catch (error) {
        console.error('Error fetching subscription details:', error);
        setSubscriptionEndDate('Error loading subscription details.');
      } finally {
        setSubscriptionLoading(false);
      }
    };

    if (user) {
      fetchSubscriptionDetails();
    }
  }, [user]); // Added subscription to dependency array if it's used to trigger re-fetch, but it's from useCredits(), so user is fine.

  const handleCheckout = async (plan) => {
    if (plan.permalink === 'free') return;

    const userEmail = user?.user?.email || user?.email;
    const token = localStorage.getItem('token');
    const decodedToken = token ? JSON.parse(atob(token.split('.')[1])) : null;
    const userId = decodedToken?.userId;
    
    if (!userEmail || !userId) {
      setError('Please log in to upgrade');
      return;
    }

    try {
      // Determine price ID based on plan and billing cycle
      let priceId;
      
      if (plan.permalink === 'pro') {
        if (billingCycle === 'monthly') {
          priceId = process.env.REACT_APP_PADDLE_PRO_MONTHLY || 'pri_01jvw9ts30k6nbwt9x4gbbbj6v';
        } else if (billingCycle === 'yearly') {
          priceId = process.env.REACT_APP_PADDLE_PRO_YEARLY || 'pri_01jwp0bckvy93r73d3bp2t2a7j';
        } else if (billingCycle === 'lifetime') {
          priceId = process.env.REACT_APP_PADDLE_PRO_LIFETIME || 'pri_01lkzf7s9rzhdqgxm4kjkpvdsj';
        }
      } else {
        if (billingCycle === 'monthly') {
          priceId = process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY || 'pri_01jwp086ffhm7zb40jzx3vpf1d';
        } else if (billingCycle === 'yearly') {
          priceId = process.env.REACT_APP_PADDLE_BUSINESS_YEARLY || 'pri_01jwp0enxztrcaxk7kw35j7vb7';
        } else if (billingCycle === 'lifetime') {
          priceId = process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME || 'pri_01lkzf7s9xzxdtbnm4kjkpvdtk';
        }
      }

      // Validate that we have a price ID
      if (!priceId) {
        console.error('Missing price ID for plan:', plan);
        setError('Failed to initialize checkout');
        return;
      }

      console.log('Initializing Paddle checkout with:', {
        priceId,
        userEmail,
        userId
      });
      
      await Paddle.Checkout.open({
        items: [{
          price_id: priceId,
          quantity: 1
        }],
        customer: {
          email: userEmail
        },
        customData: {
          user_id: userId
        },
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          locale: 'en'
        }
      });
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to initialize checkout');
    }
  };

  // Add cancel subscription handler
  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      console.log('Current subscription:', subscription);
      // Use subscription_id instead of paddle_subscription_id
      const subId = subscription.subscription_id;
      
      if (!subId) {
        console.error('No subscription ID found:', subscription);
        setError('Missing subscription ID');
        setCancelLoading(false);
        return;
      }
      
      console.log('Cancelling subscription with ID:', subId);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payments/cancel`,
        { 
          subscriptionId: subId 
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('Cancel response:', response.data);
      
      // Show success dialog with any details
      setOpenCancelDialog(false);
      setError(null);

      // Don't change the current plan as subscription is still active until end of billing period
      // setCurrentPlan('free');
      // setSubscriptionEndDate(null);
      
      // Prepare success message with any warnings
      setSuccessMessage({
        title: response.data.message || 'Subscription Cancellation Scheduled',
        message: 'Your cancellation has been processed successfully.',
        details: response.data.details || ''
      });
      
      // Show the success dialog
      setOpenSuccessDialog(true);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      console.error('Error details:', error.response?.data);
      setError(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  // Pricing data with Gumroad links
  const plans = [
    {
      name: "Free",
      permalink: "free",
      description: "Perfect for getting started with 10 monthly credits",
      price: 0,
      yearlyPrice: 0,
      lifetimePrice: 0,
      credits: "10 monthly credits",
      features: [
        "AI Post Generator",
        "Topic Generator",
        "AI Image/GIF/Carousel",
        "Banner Designer",
        "Basic Analytics & Profile Score"
      ],
      highlighted: false
    },
    {
      name: "Pro",
      permalink: "pro",
      description: "For individual professionals with 150 monthly credits",
      lifetimeDescription: "One-time payment for lifetime access to all Pro features",
      price: 18,
      yearlyPrice: 180,
      lifetimePrice: 300,
      credits: "150 monthly credits",
      features: [
        "All Free features, plus:",
        "Viral LinkedIn Posts",
        "Trending Influencers",
        "Trends & News Finder",
        "Engagement Lists (3 profiles)",
        "Full Analytics & Recommendations",
        "Full Profile Optimization Details"
      ],
      highlighted: true
    },
    {
      name: "Business",
      permalink: "business",
      description: "For teams and businesses with unlimited credits",
      price: 35,
      yearlyPrice: 350,
      lifetimePrice: 799,
      credits: "Unlimited credits",
      features: [
        "All Pro features, plus:",
        "Unlimited Engagement Lists",
        "Advanced Team Features (coming soon)"
      ],
      highlighted: false
    }
  ];

  // Helper function to determine if plan should be disabled
  const isDisabled = (planPermalink) => {
    if (!subscription) return false;
    
    // If user has business plan, disable all other plans
    if (
      subscription.price_id === process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY ||
      subscription.price_id === process.env.REACT_APP_PADDLE_BUSINESS_YEARLY ||
      subscription.price_id === process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME
    ) {
      return true;
    }
    
    // If user has pro plan, disable free and pro plans
    if (
      subscription.price_id === process.env.REACT_APP_PADDLE_PRO_MONTHLY ||
      subscription.price_id === process.env.REACT_APP_PADDLE_PRO_YEARLY ||
      subscription.price_id === process.env.REACT_APP_PADDLE_PRO_LIFETIME
    ) {
      return planPermalink === 'free' || planPermalink === 'pro' || planPermalink === 'business';
    }
    
    // If user has free plan, only disable free plan
    return planPermalink === 'free';
  };

  // Helper function to get button text
  const getButtonText = (plan) => {
    if (!subscription) return 'Choose Plan';
    
    if (
      subscription.price_id === process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY ||
      subscription.price_id === process.env.REACT_APP_PADDLE_BUSINESS_YEARLY ||
      subscription.price_id === process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME
    ) {
      return plan.permalink === 'business' ? 'Current Plan' : 'Downgrade Not Available';
    }
    
    if (
      subscription.price_id === process.env.REACT_APP_PADDLE_PRO_MONTHLY ||
      subscription.price_id === process.env.REACT_APP_PADDLE_PRO_YEARLY ||
      subscription.price_id === process.env.REACT_APP_PADDLE_PRO_LIFETIME
    ) {
      if (plan.permalink === 'pro') return 'Current Plan';
      if (plan.permalink === 'free') return 'Downgrade Not Available';
      return 'Upgrade to Business';
    }
    
    return plan.permalink === 'free' ? 'Current Plan' : 'Choose Plan';
  };

  // Helper function to get current price based on billing cycle
  const getCurrentPrice = (plan) => {
    if (billingCycle === 'yearly') return plan.yearlyPrice;
    if (billingCycle === 'lifetime') return plan.lifetimePrice;
    return plan.price;
  };

  // Helper function to get billing cycle label
  const getBillingLabel = () => {
    if (billingCycle === 'yearly') return '/year';
    if (billingCycle === 'lifetime') return '';
    return '/month';
  };

  // Combine all loading states into one
  const isLoading = loading || !user || subscriptionLoading;

  // Show LoadingScreen while loading
  if (isLoading) {
    return <LoadingScreen message="Loading pricing plans..." />;
  }

  // Show login required message if no user
  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          You need to be logged in to view this page.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/login')}>
          Login
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      position: 'relative',
      p: 3,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper'
    }}>
      <Helmet>
        <title>Pricing Plans | Instation</title>
        <meta name="description" content="Choose the perfect plan for your LinkedIn content creation and management needs with Instation's flexible pricing options." />
      </Helmet>
      
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ 
          fontFamily: 'Poppins',
          fontWeight: 600,
          mb: 2,
          color: '#0f0f0f'
        }}>
          Choose Your Plan
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Select the plan that best fits your content creation needs
        </Typography>
        
        {/* Billing Cycle Tabs */}
        <Box sx={{ 
          width: '100%', 
          display: 'flex',
          justifyContent: 'center',
          mb: 2
        }}>
          <Tabs
            value={billingCycle}
            onChange={handleBillingCycleChange}
            sx={{
              '.MuiTabs-indicator': {
                backgroundColor: '#4890fe',
              },
              '.MuiTab-root': {
                textTransform: 'none',
                minWidth: 100,
                fontWeight: 500,
                '&.Mui-selected': {
                  color: '#4890fe',
                  fontWeight: 600,
                },
              },
            }}
          >
            <Tab 
              label="Monthly" 
              value="monthly" 
              sx={{ 
                borderRadius: '4px 0 0 4px',
              }}
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Yearly
                  <Chip 
                    label="Save 20%" 
                    size="small" 
                    sx={{ 
                      ml: 1, 
                      bgcolor: billingCycle === 'yearly' ? 'rgba(72, 144, 254, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                      color: billingCycle === 'yearly' ? '#4890fe' : 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                </Box>
              }
              value="yearly"
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Lifetime
                  <Chip 
                    label="Best Value" 
                    size="small" 
                    sx={{ 
                      ml: 1, 
                      bgcolor: billingCycle === 'lifetime' ? 'rgba(75, 0, 130, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                      color: billingCycle === 'lifetime' ? '#7B2DED' : 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                </Box>
              }
              value="lifetime" 
              sx={{ 
                borderRadius: '0 4px 4px 0',
                color: billingCycle === 'lifetime' ? '#7B2DED' : 'inherit',
                '&.Mui-selected': {
                  color: '#7B2DED',
                }
              }}
            />
          </Tabs>
        </Box>
      </Box>

      {/* Subscription End Date */}
      {subscriptionEndDate && (
        <Box sx={{ 
          textAlign: 'center', 
          mb: 6,
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(72, 144, 254, 0.05)',
          border: '1px solid rgba(72, 144, 254, 0.1)',
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
            color: '#4890fe',
            fontFamily: 'Poppins',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <CalendarTodayIcon fontSize="small" />
          {subscriptionEndDate}
          </Typography>
        </Box>
      )}

      {/* Cancel Subscription Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
          Cancel Subscription
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            Are you sure you want to cancel your subscription?
          </Typography>
          <Box component="ul" sx={{ mt: 2 }}>
            <Typography component="li">You'll continue to have full access until the end of your current billing period</Typography>
            <Typography component="li">No further charges will be made after the billing period ends</Typography>
            <Typography component="li">Your account will automatically revert to the free plan when your subscription ends</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #ddd' }}>
          <Button 
            onClick={() => setOpenCancelDialog(false)}
            variant="outlined"
          >
            Keep Subscription
          </Button>
          <Button 
            onClick={handleCancelSubscription}
            color="error"
            variant="contained"
            disabled={cancelLoading}
            startIcon={cancelLoading ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
          >
            Yes, Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog with Warning */}
      <Dialog 
        open={openSuccessDialog} 
        onClose={() => setOpenSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: '#f8f9fa', 
          borderBottom: '1px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CheckCircleIcon color="success" />
          {successMessage.title}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ mb: 2 }}>
            Your cancellation has been processed successfully.
          </Typography>
          
          {successMessage.details && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'rgba(232, 245, 233, 0.8)', // Light green background for positive message
              border: '1px solid #4caf50',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}>
              <InfoIcon color="success" sx={{ mt: 0.5 }} />
              <Typography>
                {successMessage.details}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #ddd' }}>
          <Button 
            onClick={() => setOpenSuccessDialog(false)}
            variant="contained"
            color="primary"
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plans Grid */}
      <Box sx={{ 
        flex: 1,
        minHeight: 0,
        overflow: 'auto'
      }}>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          {plans
            .filter(plan => billingCycle !== 'lifetime' || plan.permalink === 'pro')
            .map((plan) => (
            <Grid item xs={12} md={billingCycle === 'lifetime' ? 12 : 4} key={plan.name}>
              <Paper 
                elevation={0}
                sx={{
                  p: 3,
                  mt: plan.highlighted ? 2 : 0,
                  height: '100%',
                  maxWidth: billingCycle === 'lifetime' ? '450px' : '100%',
                  mx: billingCycle === 'lifetime' ? 'auto' : 0,
                  boxShadow: billingCycle === 'lifetime' ? '0 5px 20px rgba(123, 45, 237, 0.1)' : 'none',
                  border: '1px solid',
                  borderColor: subscription?.status === 'active' && subscription?.price_id === (
                    plan.permalink === 'pro' 
                      ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_PRO_YEARLY : 
                         billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_PRO_LIFETIME : 
                         process.env.REACT_APP_PADDLE_PRO_MONTHLY)
                      : plan.permalink === 'business'
                        ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_BUSINESS_YEARLY : 
                           billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME : 
                           process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY)
                        : ''
                  ) ? '#38A169' : plan.highlighted ? '#4890fe' : 'divider',
                  borderWidth: subscription?.status === 'active' && subscription?.price_id === (
                    plan.permalink === 'pro' 
                      ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_PRO_YEARLY : 
                         billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_PRO_LIFETIME : 
                         process.env.REACT_APP_PADDLE_PRO_MONTHLY)
                      : plan.permalink === 'business'
                        ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_BUSINESS_YEARLY : 
                           billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME : 
                           process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY)
                        : ''
                  ) ? '2px' : '1px',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  bgcolor: plan.highlighted ? 'rgba(72, 144, 254, 0.03)' : subscription?.status === 'active' && subscription?.price_id === (
                    plan.permalink === 'pro' 
                      ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_PRO_YEARLY : 
                         billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_PRO_LIFETIME : 
                         process.env.REACT_APP_PADDLE_PRO_MONTHLY)
                      : plan.permalink === 'business'
                        ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_BUSINESS_YEARLY : 
                           billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME : 
                           process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY)
                        : ''
                  ) ? 'rgba(56, 161, 105, 0.03)' : 'background.paper',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
                  }
                }}
              >
                {plan.highlighted && (
                  <Box sx={{
                    position: 'absolute',
                    top: -15,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: billingCycle === 'lifetime' ? '#7B2DED' : '#4890fe',
                    color: 'white',
                    py: 0.5,
                    px: 2,
                    borderRadius: 10,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    zIndex: 20,
                    whiteSpace: 'nowrap',
                    boxShadow: billingCycle === 'lifetime' 
                      ? '0 2px 8px rgba(123, 45, 237, 0.3)' 
                      : '0 2px 8px rgba(72, 144, 254, 0.3)',
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {billingCycle === 'lifetime' ? 'Lifetime Deal' : 'Most Popular'}
                  </Box>
                )}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ 
                    mb: 1, 
                    fontWeight: 700,
                    color: plan.highlighted ? '#4890fe' : '#2D3748'
                  }}>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 2,
                    color: 'text.secondary'
                  }}>
                    {billingCycle === 'lifetime' && plan.lifetimeDescription ? plan.lifetimeDescription : plan.description}
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    mb: 1, 
                    fontWeight: 700,
                    color: plan.highlighted ? '#4890fe' : '#2D3748',
                    display: 'flex',
                    alignItems: 'baseline'
                  }}>
                    ${getCurrentPrice(plan)}
                    <Typography component="span" variant="body1" sx={{ 
                      color: 'text.secondary',
                      ml: 0.5,
                      fontWeight: 400
                    }}>
                      {plan.price > 0 ? getBillingLabel() : ''}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 2,
                    color: 'text.secondary',
                    fontWeight: 500
                  }}>
                    {billingCycle === 'lifetime' ? (plan.permalink === 'free' ? "10 monthly credits" : "Unlimited lifetime access") : plan.credits}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, mb: 3 }}>
                  <Typography component="ul" sx={{ 
                    pl: 0,
                    listStyleType: 'none',
                    m: 0
                  }}>
                    {plan.features.map((feature, index) => (
                      <Box 
                        component="li" 
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1.5,
                          color: '#4A5568'
                        }}
                      >
                        <CheckCircleIcon 
                          sx={{ 
                            mr: 1.5, 
                            fontSize: 20,
                            color: plan.highlighted ? '#4890fe' : '#38A169'
                          }} 
                        />
                        {feature}
                      </Box>
                    ))}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  disabled={isDisabled(plan.permalink) || (billingCycle === 'lifetime' && plan.permalink === 'free')}
                  onClick={() => handleCheckout(plan)}
                  sx={{ 
                    mt: 2,
                    py: 1.5,
                    bgcolor: subscription?.status === 'active' && subscription?.price_id === (
                      plan.permalink === 'pro' 
                        ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_PRO_YEARLY : 
                           billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_PRO_LIFETIME : 
                           process.env.REACT_APP_PADDLE_PRO_MONTHLY)
                        : plan.permalink === 'business'
                          ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_BUSINESS_YEARLY : 
                             billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME : 
                             process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY)
                          : ''
                    ) ? '#38A169' : plan.highlighted ? '#4890fe' : 'white',
                    color: (subscription?.status === 'active' && subscription?.price_id === (
                      plan.permalink === 'pro' 
                        ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_PRO_YEARLY : 
                           billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_PRO_LIFETIME : 
                           process.env.REACT_APP_PADDLE_PRO_MONTHLY)
                        : plan.permalink === 'business'
                          ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_BUSINESS_YEARLY : 
                             billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME : 
                             process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY)
                          : ''
                    ) || plan.highlighted) ? 'white' : '#4A5568',
                    border: '1px solid',
                    borderColor: subscription?.status === 'active' && subscription?.price_id === (
                      plan.permalink === 'pro' 
                        ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_PRO_YEARLY : 
                           billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_PRO_LIFETIME : 
                           process.env.REACT_APP_PADDLE_PRO_MONTHLY)
                        : plan.permalink === 'business'
                          ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_BUSINESS_YEARLY : 
                             billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME : 
                             process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY)
                          : ''
                    ) ? '#38A169' : plan.highlighted ? '#4890fe' : 'divider',
                    '&:hover': {
                      bgcolor: subscription?.status === 'active' && subscription?.price_id === (
                        plan.permalink === 'pro' 
                          ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_PRO_YEARLY : 
                             billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_PRO_LIFETIME : 
                             process.env.REACT_APP_PADDLE_PRO_MONTHLY)
                          : plan.permalink === 'business'
                            ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_BUSINESS_YEARLY : 
                               billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME : 
                               process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY)
                            : ''
                      ) ? '#2F855A' : plan.highlighted ? '#3a7ad9' : 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)'
                    }
                  }}
                >
                  {getButtonText(plan)}
                </Button>
                
                {/* Current plan indicator */}
                {subscription?.status === 'active' && subscription?.price_id === (
                  plan.permalink === 'pro' 
                    ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_PRO_YEARLY : 
                       billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_PRO_LIFETIME : 
                       process.env.REACT_APP_PADDLE_PRO_MONTHLY)
                    : plan.permalink === 'business'
                      ? (billingCycle === 'yearly' ? process.env.REACT_APP_PADDLE_BUSINESS_YEARLY : 
                         billingCycle === 'lifetime' ? process.env.REACT_APP_PADDLE_BUSINESS_LIFETIME : 
                         process.env.REACT_APP_PADDLE_BUSINESS_MONTHLY)
                      : ''
                ) && !subscription?.cancellation_date && billingCycle !== 'lifetime' && (
                  <>
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={() => setOpenCancelDialog(true)}
                      sx={{
                        mt: 1,
                        mb: -1,
                        fontSize: '0.75rem',
                        textTransform: 'none'
                      }}
                    >
                      Cancel Subscription
                    </Button>
                    <Box sx={{
                      mt: 1.5, 
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5
                    }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: '#38A169',
                        animation: 'pulse 2s infinite'
                      }} />
                      <Typography variant="caption" sx={{ 
                        color: '#38A169',
                        fontWeight: 600
                      }}>
                        Your current active plan
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          <Typography variant="body2">
            {billingCycle === 'lifetime' ? 
              "Lifetime plans include a 30-day money-back guarantee." : 
              "All plans include a 7-day money-back guarantee. No questions asked."}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Need help choosing? <Link href="/contact" sx={{ color: '#4890fe', fontWeight: 500 }}>Contact us</Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PricingPlans; 